import { Box, Typography } from '@mui/material';

const PANEL_MAX_WIDTH = 1235;
const INNER_GRADIENT =
  'radial-gradient(ellipse 61.75% 25.3% at 50% 50%, #3d03a1 63.46%, #2f027e 81.73%, #22005c 100%)';

const sectionHeadingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: 20,
  lineHeight: '32px',
  letterSpacing: '0.8px',
  color: '#fff',
  m: 0,
};

const bodyTextSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: 16,
  lineHeight: '28px',
  letterSpacing: '1.5px',
  color: '#fff',
  m: 0,
};

const richContentSx = {
  maxWidth: 1159,
  mx: 'auto',
  color: '#fff',
  '& h2, & h3': {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontSize: 20,
    lineHeight: '32px',
    letterSpacing: '0.8px',
    color: '#fff',
    m: 0,
    mt: 3,
    mb: 1.5,
    '&:first-of-type': { mt: 0 },
  },
  '& h3:first-of-type': {
    fontWeight: 500,
    fontSize: 24,
    textAlign: 'center',
    mb: 3,
  },
  '& p': {
    ...bodyTextSx,
    mb: 1.5,
  },
  '& ul, & ol': {
    m: 0,
    mb: 1.5,
    pl: 3,
    '& li': bodyTextSx,
    '& li p': { m: 0 },
  },
  '& a': {
    color: '#ff00f2',
    textDecoration: 'underline',
  },
  '& strong': {
    fontWeight: 700,
  },
};

function LegalList({ items = [] }) {
  if (!items.length) return null;

  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        pl: 3,
        listStyleType: 'disc',
        '& li': {
          ...bodyTextSx,
          mb: 0,
        },
      }}
    >
      {items.map((item) => (
        <Box component="li" key={item}>
          {item}
        </Box>
      ))}
    </Box>
  );
}

function LegalSection({ section }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography component="h2" sx={sectionHeadingSx}>
        {section.title}
      </Typography>

      {section.paragraphs?.map((paragraph) => (
        <Typography key={paragraph} sx={bodyTextSx}>
          {paragraph}
        </Typography>
      ))}

      <LegalList items={section.list} />
    </Box>
  );
}

/** Figma 11604:1812 — legal document panel */
export default function LegalDocumentPanel({
  title,
  companyName,
  sections = [],
  htmlContent = '',
}) {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: PANEL_MAX_WIDTH,
        mx: 'auto',
        borderRadius: '36px 36px 0 0',
        border: '1px solid #d6a7ff',
        background: 'linear-gradient(180deg, #8f51fb 0%, #5c1ec8 34.483%)',
        boxShadow: '0 0 16px 6px rgba(183, 89, 255, 0.35)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: { xs: 'calc(100vh - 120px)', md: 'calc(100vh - 160px)' },
        minHeight: { xs: 480, md: 640 },
      }}
    >
      <Box
        sx={{
          minHeight: 64,
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2.5, sm: '38px' },
          py: 2,
          flexShrink: 0,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: { xs: 20, sm: 24 },
            lineHeight: '32px',
            letterSpacing: '0.8px',
            color: '#fff',
            textShadow: '0 2px 0 rgba(0, 0, 0, 0.8)',
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          background: INNER_GRADIENT,
          boxShadow: 'inset 0 12px 10px rgba(43, 0, 118, 0.39)',
          px: { xs: 2.5, sm: '38px' },
          pt: 2.5,
          pb: { xs: 3, sm: 4 },
        }}
      >
        {htmlContent ? (
          <Box sx={richContentSx} dangerouslySetInnerHTML={{ __html: htmlContent }} />
        ) : (
          <Box sx={{ maxWidth: 1159, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 7 }}>
            {companyName ? (
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: 24,
                  lineHeight: '32px',
                  letterSpacing: '0.8px',
                  color: '#fff',
                  textAlign: 'center',
                }}
              >
                {companyName}
              </Typography>
            ) : null}

            {sections.map((section) => (
              <LegalSection key={section.id} section={section} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
